import useSWR, { SWRConfiguration } from 'swr'
import fetcher from '@lib/fetcher'
import { useRouter } from 'next/router'
import routes from '@config/routes'
import { useAuth } from '@contexts/auth'
import React, { useState, useEffect } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from '@contexts/translation'

interface ErrorResponse {
  status: number
  message?: string
  details?: string
  data?: any
  [key: string]: any
}

interface FetchResponse<T> {
  data: T | undefined
  isLoading: boolean
  isError: ErrorResponse | null
  mutate: (data?: T | Promise<T>) => Promise<any>
}

// Default fallback data for known endpoints that may 404
const FALLBACK_DATA = {
  tenants: { id: null, name: '', results: [] },
  'tenants/': { id: null, name: '', results: [] },
  'stats/invoicing_planning': { results: [], count: 0 },
  'organizations/membership': { results: [], organizations: [] },
  operations: { results: [], count: 0 },
  'operations/': { results: [], count: 0 },
  'operations/onboarding': { is_completed: false, steps: [] },
  suppliers: { results: [], count: 0 },
  invoices: { results: [], count: 0 }
}

/**
 * Simple response handler similar to the one from the other project
 */
const handleApiResponse = (
  response: any,
  onSuccess?: (data: any) => void,
  onError?: () => void,
  showNotifications = false,
  t?: any,
  dictionary?: any,
  enqueueSnackbar?: any
) => {
  // Handle success responses
  if (
    response?.status === 200 ||
    response?.status === 201 ||
    response?.status === 204
  ) {
    if (onSuccess) {
      onSuccess(response?.data)
    }

    if (showNotifications && enqueueSnackbar) {
      enqueueSnackbar('Success', { variant: 'success' })
    }
    return true
  }
  // Handle error responses
  else {
    if (onError) {
      onError()
    }

    if (showNotifications && enqueueSnackbar) {
      const errorMessage = getErrorMessage(response, t, dictionary)

      if (response?.status === 400 || response?.status === 401) {
        enqueueSnackbar(errorMessage, { variant: 'warning' })
      } else if (response?.status === 403) {
        enqueueSnackbar(errorMessage, { variant: 'warning' })
      } else if (response?.status === 404) {
        enqueueSnackbar('Not found', { variant: 'warning' })
      } else if (response?.status !== 503) {
        // Skip 503 errors
        enqueueSnackbar('Server error', { variant: 'error' })
      }
    }
    return false
  }
}

/**
 * Helper to extract error messages from responses
 */
const getErrorMessage = (response: any, t?: any, dictionary?: any): string => {
  // Check for structured errors
  if (response?.data?.errors && response.data.errors.length > 0) {
    const firstError = response.data.errors[0]
    return (
      firstError.message + (firstError.field ? ` (${firstError.field})` : '')
    )
  }

  // Default error messages
  if (response?.status === 400) {
    return dictionary
      ? `${dictionary.error} - ${dictionary.bad_request}`
      : t
      ? `${t('error')} - ${t('bad_request')}`
      : 'Bad request'
  }
  if (response?.status === 401) {
    return dictionary
      ? `${dictionary.error} - ${dictionary.not_authorized}`
      : t
      ? `${t('error')} - ${t('not_authorized')}`
      : 'Not authorized'
  }
  if (response?.status === 403) {
    return dictionary
      ? `${dictionary.error} - ${dictionary.permission_error}`
      : t
      ? `${t('error')} - ${t('permission_error')}`
      : 'Permission error'
  }
  if (response?.status === 404) {
    return dictionary
      ? `${dictionary.error} - ${dictionary.not_found}`
      : t
      ? `${t('error')} - ${t('not_found')}`
      : 'Not found'
  }

  return dictionary
    ? dictionary.server_error
    : t
    ? t('server_error')
    : 'Server error'
}

/**
 * Improved data fetching hook with response handling pattern
 */
export const useFetchData = <T = any>(
  url: string | null,
  options?: SWRConfiguration & {
    showNotifications?: boolean
  }
): FetchResponse<T> => {
  const router = useRouter()
  const { logout } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const { t, dictionary } = useTranslation()

  // Explicit loading state management
  const [isLoading, setIsLoading] = useState<boolean>(!!url)
  const [errorState, setErrorState] = useState<ErrorResponse | null>(null)

  // Extract options
  const { showNotifications = false, ...swrOptions } = options || {}

  // SWR with simplified fetcher
  const { data, error, mutate } = useSWR<T>(
    url,
    async url => {
      try {
        // console.log(`Fetching: ${url}`)
        setIsLoading(true)
        setErrorState(null)

        const response = await fetcher(url)

        // Use response handler pattern for consistent handling
        handleApiResponse(
          { status: 200, data: response },
          () => {
            setIsLoading(false)
          },
          undefined,
          false // Don't show notifications for GET requests by default
        )

        return response
      } catch (error: any) {
        // console.error(`Error fetching ${url}:`, error)

        // Extract structured error information
        const errorResponse = {
          status: error?.status || 500,
          message: error?.message || 'Unknown error',
          data: error?.details || error
        }

        // Handle 404s with fallback data
        if (errorResponse.status === 404) {
          // Find fallback data for this URL
          for (const [pattern, fallbackData] of Object.entries(FALLBACK_DATA)) {
            if (url.includes(pattern)) {
              // console.log(`Using fallback data for ${url}`)
              setIsLoading(false)
              return fallbackData as T
            }
          }
        }

        // Use response handler pattern for errors too
        handleApiResponse(
          errorResponse,
          undefined,
          () => {
            setIsLoading(false)
            setErrorState(errorResponse)
          },
          showNotifications,
          t,
          dictionary,
          enqueueSnackbar
        )

        throw errorResponse
      }
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 3000,
      errorRetryCount: 1,
      ...swrOptions
    }
  )

  // Reset loading state on data or error changes
  useEffect(() => {
    if (data || error) {
      setIsLoading(false)
    }
  }, [data, error])

  // Force loading state to end after timeout
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        // console.log('Force ending loading state after timeout')
        setIsLoading(false)
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [isLoading])

  // Handle 401 (unauthorized) errors
  useEffect(() => {
    const status = error?.status || errorState?.status

    if (status === 401) {
      const isAuthRoute =
        router?.asPath.includes(routes.account.login) ||
        router?.asPath.includes(routes.account.registration) ||
        router?.asPath.includes(routes.account.signup) ||
        router?.asPath.includes(routes.account.resetPassword) ||
        router?.asPath.includes(routes.account.activation) ||
        router?.asPath.includes(routes.account.acceptInvitation) ||
        router?.asPath.includes(routes.account.onboarding)

      if (!isAuthRoute) {
        console.warn('Authentication token expired, logging out')
        logout()
      }
    }
  }, [error, errorState, router, logout])

  return {
    data,
    isLoading,
    isError: errorState || (error as ErrorResponse),
    mutate
  }
}
